import { getToken } from './tokenManager';

// WebSocket ราคา realtime ของ Gateway (หุ้น + crypto ใช้ connection เดียวกัน)
// ตอนนี้ใช้กับ crypto เท่านั้น หุ้นยังดึงแบบเดิมทุก 14 วิ เพราะข้อมูลหุ้นใน WS มีแค่ price
// ไม่มี regular_close / extended_price / market_session เหมือน snapshot
const GATEWAY_BASE_URL = 'https://gateway.wethaiinvest.com';

// Server จำกัด symbol รวมต่อ connection ไว้ที่ 100 (REALTIME_WS_MAX_SYMBOLS)
export const REALTIME_MAX_SYMBOLS = 100;
const PING_INTERVAL_MS = 25000;
const RECONNECT_MIN_MS = 1000;
const RECONNECT_MAX_MS = 30000;

export interface RealtimeQuote {
  symbol: string;
  price: number;          // ใช้แสดงผลเท่านั้น
  priceDecimal?: string;  // crypto: ค่าที่ถูกต้องสำหรับคำนวณ
  assetClass?: string;
  source?: string;
  stale?: boolean;        // ไม่ส่งมาเมื่อเป็น false
  timestamp?: number;     // เวลา provider (ms)
}

interface RealtimeTicket {
  ticket: string;
  expiresIn: number;
  wsUrl: string;
}

async function requestRealtimeTicket(): Promise<RealtimeTicket> {
  const res = await fetch(`${GATEWAY_BASE_URL}/v1/realtime/ticket`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) throw new Error(`Realtime ticket error: ${res.status} ${res.statusText}`);
  const data = await res.json();
  if (!data?.data?.ticket || !data?.data?.wsUrl) throw new Error('Realtime ticket response missing ticket/wsUrl');
  return data.data;
}

type QuotesListener = (quotes: RealtimeQuote[]) => void;
type StatusListener = (connected: boolean) => void;

class RealtimeSocket {
  private ws: WebSocket | null = null;
  private desired = new Set<string>();   // symbol ที่ UI ต้องการ
  private subscribed = new Set<string>(); // symbol ที่ส่ง subscribe ไปแล้วใน connection ปัจจุบัน
  private connecting = false;
  private connected = false;
  private reconnectDelay = RECONNECT_MIN_MS;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private requestSeq = 0;
  private quotesListeners = new Set<QuotesListener>();
  private statusListeners = new Set<StatusListener>();

  constructor() {
    // กลับมาที่แท็บ/แอป (มือถือ, Telegram mini app) ให้ต่อใหม่ทันที ไม่ต้องรอ backoff
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && !this.connected && this.desired.size > 0) {
          this.clearReconnect();
          this.reconnectDelay = RECONNECT_MIN_MS;
          this.connect();
        }
      });
    }
  }

  onQuotes(listener: QuotesListener) {
    this.quotesListeners.add(listener);
    return () => { this.quotesListeners.delete(listener); };
  }

  onStatus(listener: StatusListener) {
    this.statusListeners.add(listener);
    listener(this.connected);
    return () => { this.statusListeners.delete(listener); };
  }

  isConnected() {
    return this.connected;
  }

  // ตั้งรายการ symbol ที่ต้องการ (ตัดที่ 100 ตัว ตัวที่เกินให้ caller ดึงแบบ polling ต่อ)
  setSymbols(symbols: string[]): string[] {
    const accepted = Array.from(new Set(symbols)).slice(0, REALTIME_MAX_SYMBOLS);
    this.desired = new Set(accepted);

    if (this.desired.size === 0) {
      this.close();
      return accepted;
    }
    if (this.connected) this.syncSubscriptions();
    else this.connect();
    return accepted;
  }

  private async connect() {
    if (this.connecting || this.connected || this.desired.size === 0) return;
    if (!getToken()) return;
    this.connecting = true;

    try {
      // ticket ใช้ได้ครั้งเดียว อายุ ~30 วิ ต้องขอใหม่ทุกครั้งที่ต่อ
      const { ticket, wsUrl } = await requestRealtimeTicket();
      // ระหว่างรอ ticket อาจไม่มี symbol ที่ต้องการแล้ว
      if (this.desired.size === 0) {
        this.connecting = false;
        return;
      }
      const sep = wsUrl.includes('?') ? '&' : '?';
      const ws = new WebSocket(`${wsUrl}${sep}ticket=${encodeURIComponent(ticket)}`);
      this.ws = ws;

      ws.onopen = () => {
        this.connecting = false;
        this.setConnected(true);
        this.reconnectDelay = RECONNECT_MIN_MS;
        this.subscribed.clear();
        this.syncSubscriptions();
        this.startPing();
      };
      ws.onmessage = event => this.handleMessage(event.data);
      ws.onerror = () => {
        // รายละเอียดจะตามมาใน onclose
      };
      ws.onclose = event => {
        if (this.ws !== ws) return;
        this.ws = null;
        this.connecting = false;
        this.stopPing();
        this.subscribed.clear();
        this.setConnected(false);
        if (event.code !== 1000) console.warn('Realtime socket closed:', event.code, event.reason);
        this.scheduleReconnect();
      };
    } catch (err) {
      console.error('Realtime socket connect failed:', err);
      this.connecting = false;
      this.scheduleReconnect();
    }
  }

  private close() {
    this.clearReconnect();
    this.stopPing();
    const ws = this.ws;
    this.ws = null;
    this.subscribed.clear();
    this.connecting = false;
    if (ws) ws.close(1000, 'no symbols');
    this.setConnected(false);
  }

  private scheduleReconnect() {
    if (this.desired.size === 0 || this.reconnectTimer) return;
    // แท็บที่ซ่อนอยู่ไม่ต้องต่อใหม่ รอ visibilitychange แทน
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    const delay = this.reconnectDelay;
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, RECONNECT_MAX_MS);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private clearReconnect() {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private startPing() {
    this.stopPing();
    this.pingTimer = setInterval(() => this.send({ op: 'ping' }), PING_INTERVAL_MS);
  }

  private stopPing() {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  private send(payload: Record<string, unknown>) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(payload));
    }
  }

  private nextRequestId(prefix: string) {
    this.requestSeq += 1;
    return `${prefix}-${this.requestSeq}`;
  }

  // ส่งเฉพาะส่วนต่าง (subscribe ตัวใหม่ / unsubscribe ตัวที่เลิกใช้)
  private syncSubscriptions() {
    const toAdd = Array.from(this.desired).filter(s => !this.subscribed.has(s));
    const toRemove = Array.from(this.subscribed).filter(s => !this.desired.has(s));

    if (toRemove.length > 0) {
      this.send({ op: 'unsubscribe', requestId: this.nextRequestId('unsub'), symbols: toRemove });
      toRemove.forEach(s => this.subscribed.delete(s));
    }
    if (toAdd.length > 0) {
      this.send({ op: 'subscribe', requestId: this.nextRequestId('sub'), symbols: toAdd });
      toAdd.forEach(s => this.subscribed.add(s));
    }
  }

  private handleMessage(raw: unknown) {
    if (typeof raw !== 'string') return;
    let msg: any;
    try {
      msg = JSON.parse(raw);
    } catch {
      return;
    }

    if (msg?.type === 'error' || msg?.error) {
      console.warn('Realtime socket error message:', msg);
      return;
    }

    // ทั้ง snapshot (หลัง subscribe) และ prices (อัปเดตต่อเนื่อง) ส่งรายการราคาใน data
    const items: any[] = Array.isArray(msg?.data) ? msg.data : Array.isArray(msg?.data?.data) ? msg.data.data : [];
    if (items.length === 0) return;

    const quotes: RealtimeQuote[] = items
      .filter(item => item && typeof item.symbol === 'string' && typeof item.price === 'number')
      .map(item => ({
        symbol: item.symbol,
        price: item.price,
        priceDecimal: item.priceDecimal,
        assetClass: item.assetClass,
        source: item.source,
        stale: item.stale === true,
        timestamp: item.timestamp ?? msg.timestamp,
      }));

    if (quotes.length > 0) this.quotesListeners.forEach(l => l(quotes));
  }

  private setConnected(value: boolean) {
    if (this.connected === value) return;
    this.connected = value;
    this.statusListeners.forEach(l => l(value));
  }
}

export const realtimeSocket = new RealtimeSocket();
